import React, { useEffect, useMemo, useState } from 'react';
import {
  formatPaperRaw,
  formatUcatBand,
  formatUcatTotal,
  loadUcatPaperScores,
  loadUcatTarget,
  resetUcatSittingProgress,
  saveUcatPaperScore,
  saveUcatTarget,
  sittingScoreFromUcatPapers,
  ucatStatusFromDbPapers,
} from '../utils/ucatScore';
import useWorkspacePaperProgress, { useUserTarget } from '../hooks/useWorkspacePaperProgress';
import { UCAT_SUBTEST_KEYS } from '../utils/ucatSubtests';
import { collectTopicPaperIds, topicAttemptRowFromMap, topicAttemptStatusRequestKey, topicRowAction } from '../utils/topicAttemptRow';
import { firstPopulatedMockTier } from '../utils/firstPopulatedMockTier';
import { useAuth } from '../../auth/hooks/useAuth';
import PracticeTopicsNav from './PracticeTopicsNav';
import PracticeTopicsPages from './PracticeTopicsPages';
import { topicPapersContent } from './TopicPapersBlock';
import {
  SUBTOPIC_PAPERS_LABEL,
  SUBTOPIC_PAPERS_ALL_PAGE,
  SUBTOPIC_TIER_PAGES,
  SUBTOPIC_PAGE_TITLES,
  subtopicPapersOfTier,
} from '../utils/subtopicPapers';
import PracticeTopicsOverview from './PracticeTopicsOverview';
import useSectionPaperTierProgress from '../hooks/useSectionPaperTierProgress';
import { PRACTICE_TOPIC_PAGE_TITLES, practiceProgressSummary } from '../utils/practiceTopics';
import './UcatWorkspace.css';
import UcatScoreCalculator from './UcatScoreCalculator';
import MedSchoolsPage from './MedSchoolsPage';

const PAGES = {
  overview: 'Overview',
  ...PRACTICE_TOPIC_PAGE_TITLES,
  'mock-all': 'Mock Tests',
  'mock-beginnings': 'Mock Tests: Beginnings',
  'mock-foundations': 'Mock Tests: Foundations',
  'mock-beyond': 'Mock Tests: Beyond',
  past: 'Past Papers',
  'score-calculator': 'Score calculator',
  'med-schools': 'Med Schools',
};

const TARGET_OPTIONS = [
  { value: 2900, band: 2, label: 'University of Cambridge — Medicine (MB BChir)' },
  { value: 2950, band: 2, label: 'University of Oxford — Medicine (BM BCh)' },
  { value: 2850, band: 2, label: 'Imperial College London — Medicine (MBBS)' },
  { value: 2880, band: 2, label: "King's College London (KCL) — Medicine (MBBS)" },
  { value: 2850, band: 2, label: 'University College London (UCL) — Medicine (MBBS)' },
  { value: 2910, band: 2, label: 'University of Bristol — Medicine (MB ChB)' },
  { value: 2750, band: 2, label: 'University of Manchester — Medicine (MBChB)' },
  { value: 2820, band: 2, label: 'University of Birmingham — Medicine (MBChB)' },
  { value: 2850, band: 2, label: 'University of Edinburgh — MBChB Medicine' },
  { value: 2780, band: 2, label: 'University of Glasgow — MBChB Medicine' },
];

function paperIds(sitting) {
  return UCAT_SUBTEST_KEYS.map((key) => sitting?.papers?.[key]?.id).filter(Boolean);
}

function sittingLabel(sitting, index, kind) {
  if (kind === 'past') {
    const year = String(sitting?.title || '').match(/\b(20\d{2})\b/);
    return year ? year[1] : sitting?.title || `Paper ${index + 1}`;
  }
  return `Test ${index + 1}`;
}

function StatusPill({ status }) {
  const map = {
    done: ['completed', 'Completed'],
    progress: ['in-progress', 'In progress'],
    none: ['not-started', 'Not started'],
  };
  const [cls, label] = map[status] || map.none;
  return <span className={`ucat-ws-pill ${cls}`}>{label}</span>;
}

function ModeCell({ mode, status }) {
  if (status === 'none' || !mode) return '—';
  // 'timed_extra' (UCAT's 25%-extra-time mode) reads as plain "Timed" here —
  // this column isn't meant to distinguish extra time from standard timed.
  return <span className="ucat-ws-mode">{(mode === 'timed' || mode === 'timed_extra') ? 'Timed' : 'Untimed'}</span>;
}

function avgTotals(totals) {
  const nums = totals.filter((t) => t != null && Number.isFinite(Number(t))).map(Number);
  if (!nums.length) return null;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
}

function modalBandOf(bands) {
  const nums = bands.filter((b) => b != null && Number.isFinite(Number(b))).map(Number);
  if (!nums.length) return null;
  const counts = new Map();
  nums.forEach((b) => counts.set(b, (counts.get(b) || 0) + 1));
  let best = null;
  let bestCount = -1;
  counts.forEach((count, band) => {
    if (count > bestCount || (count === bestCount && band < best)) {
      best = band;
      bestCount = count;
    }
  });
  return best;
}

function sittingHasAllPapers(sitting) {
  return Boolean(sitting?.papers) && UCAT_SUBTEST_KEYS.every((key) => sitting.papers[key]);
}

function enrichSittings(sittings, scoresMap, dbPapers = {}) {
  return (sittings || [])
    .filter(sittingHasAllPapers)
    .map((sitting, index) => {
      const idsByKey = {};
      UCAT_SUBTEST_KEYS.forEach((key) => { idsByKey[key] = sitting.papers[key]?.id; });
      const scored = sittingScoreFromUcatPapers(idsByKey, scoresMap);
      let status = ucatStatusFromDbPapers(paperIds(sitting), dbPapers);
      if (status === 'none') {
        status = scored.status;
        if (status === 'none' && sitting.completed) status = 'done';
      }
      return { ...sitting, index, scored, status };
    });
}

function tierStats(rows) {
  const done = rows.filter((r) => r.status === 'done').length;
  const totals = rows.map((r) => r.scored.total);
  return {
    done,
    total: rows.length,
    avg: avgTotals(totals),
    pct: rows.length ? Math.round((done / rows.length) * 100) : 0,
  };
}

const clamp = (n) => Math.max(0, Math.min(100, n));
const pctOf = (score) => clamp(((score - 1200) / 2400) * 100);

const UcatWorkspace = ({
  onBack,
  mockSittings = [],
  pastSittings = [],
  sectionPapers = [],
  subtopicPapers = [],
  topicSections = [],
  isPartUnlocked,
  onStartSetPaper,
  onStartTopicPaper,
  topicStatus = 'ready',
  onContinueSitting,
  onReviewSitting,
  onRequestUpgrade,
}) => {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [page, setPage] = useState('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scoreTick, setScoreTick] = useState(0);
  const [toast, setToast] = useState(null);
  const [target, setTarget] = useUserTarget(loadUcatTarget, saveUcatTarget, userId);

  useEffect(() => {
    document.body.classList.add('exam-workspace-open');
    return () => document.body.classList.remove('exam-workspace-open');
  }, []);

  const statusRequestKey = topicAttemptStatusRequestKey([
    ...[...mockSittings, ...pastSittings].flatMap((s) => (
      UCAT_SUBTEST_KEYS.map((key) => s?.papers?.[key]?.id).filter(Boolean)
    )),
    ...collectTopicPaperIds(topicSections),
  ]);

  const {
    dbPapers,
    scoresMap,
    completedList,
  } = useWorkspacePaperProgress('UCAT', statusRequestKey, {
    userId,
    loadScores: loadUcatPaperScores,
    saveScore: saveUcatPaperScore,
    cacheTick: scoreTick,
  });



  const beginnings = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.papers?.verbal_reasoning?.tier) === 'beginnings'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );
  const foundations = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.papers?.verbal_reasoning?.tier) === 'foundations'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );
  const beyond = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.papers?.verbal_reasoning?.tier) === 'beyond'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );

  const mocksGoto = useMemo(
    () => firstPopulatedMockTier(beginnings, foundations, beyond),
    [beginnings, foundations, beyond]
  );
  const past = useMemo(
    () => enrichSittings(pastSittings, scoresMap, dbPapers),
    [pastSittings, scoresMap, dbPapers]
  );
  const topicCount = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce((n, t) => n + (t.params?.length || 0), 0),
      0
    ),
    [topicSections]
  );
  const topicsDone = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce(
        (n, t) => n + (t.params || []).filter((p) => completedList.includes(p.paper_id)).length,
        0
      ),
      0
    ),
    [topicSections, completedList]
  );

  // Section papers are listed under Practice Topics (Foundations and Beyond), so the
  // Overview's Practice Topics rows count them with the topics.
  const sectionProgress = useSectionPaperTierProgress('UCAT', sectionPapers, completedList);
  const practiceTopicsRow = practiceProgressSummary({ topicsDone, topicCount, papers: sectionProgress.all });

  const overview = useMemo(() => {
    const allMocks = [...beginnings, ...foundations, ...beyond];
    const all = [...allMocks, ...past];
    const graded = all.filter((r) => r.scored.total != null);
    const currentAvg = avgTotals(graded.map((r) => r.scored.total));
    const modalBand = modalBandOf(graded.map((r) => r.scored.band));

    let totalQuestions = 0;
    let attempted = 0;
    let correct = 0;
    all.forEach((row) => {
      totalQuestions += row.questionCount || 0;
      UCAT_SUBTEST_KEYS.forEach((key) => {
        const s = row.scored.subtests?.[key];
        if (!s || s.total == null) return;
        attempted += Number(s.total) || 0;
        correct += Number(s.correct) || 0;
      });
    });

    // 'timed_extra' counts as timed here too — this breakdown isn't meant to
    // distinguish extra time from standard timed (see ModeCell above).
    const timed = graded.filter((r) => r.scored.mode === 'timed' || r.scored.mode === 'timed_extra');
    const untimed = graded.filter((r) => r.scored.mode === 'untimed');
    const mocksDone = allMocks.filter((r) => r.status === 'done').length;
    const pastDone = past.filter((r) => r.status === 'done').length;

    return {
      currentAvg,
      modalBand,
      totalQuestions,
      attempted,
      successRate: attempted > 0 ? Math.round((correct / attempted) * 100) : null,
      scoredAttempts: graded.length,
      beginnings: tierStats(beginnings),
      foundations: tierStats(foundations),
      beyond: tierStats(beyond),
      mocks: {
        done: mocksDone,
        total: allMocks.length,
        avg: avgTotals(allMocks.map((r) => r.scored.total)),
        pct: allMocks.length ? Math.round((mocksDone / allMocks.length) * 100) : 0,
      },
      past: {
        done: pastDone,
        total: past.length,
        avg: avgTotals(past.map((r) => r.scored.total)),
        pct: past.length ? Math.round((pastDone / past.length) * 100) : 0,
      },
      timed: {
        count: timed.length,
        total: graded.length,
        avg: avgTotals(timed.map((r) => r.scored.total)),
        pct: graded.length ? Math.round((timed.length / graded.length) * 100) : 0,
      },
      untimed: {
        count: untimed.length,
        total: graded.length,
        avg: avgTotals(untimed.map((r) => r.scored.total)),
        pct: graded.length ? Math.round((untimed.length / graded.length) * 100) : 0,
      },
    };
  }, [beginnings, foundations, beyond, past]);

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(''), 2600);
  };

  const goTo = (id) => {
    setPage(id);
    setMobileOpen(false);
  };

  const startSitting = (sitting, options = {}) => {
    const paper = sitting.papers?.verbal_reasoning;
    if (!paper) return;
    const unlocked = isPartUnlocked ? isPartUnlocked(paper) : true;
    if (!unlocked) {
      if (onRequestUpgrade) onRequestUpgrade(paper);
      return;
    }
    onStartSetPaper(
      'UCAT',
      sitting.startId,
      sitting.startCatalogId,
      { pearsonSitting: true, ucatSiblings: sitting.ucatSiblings, title: sitting.title },
      options
    );
  };

  const handleRetake = (sitting) => {
    resetUcatSittingProgress(userId, paperIds(sitting));
    setScoreTick((n) => n + 1);
    startSitting(sitting, { forceNew: true });
  };

  const handleContinue = (sitting) => {
    if (typeof onContinueSitting === 'function' && sitting) {
      onContinueSitting(sitting);
      return;
    }
    showToast('Resume isn’t available yet — use Start or Retake for a new attempt.');
  };

  const handleReview = (sitting) => {
    if (typeof onReviewSitting === 'function' && sitting) {
      onReviewSitting(sitting);
      return;
    }
    showToast('Review isn’t available yet.');
  };

  const targetValue = Number(target?.value) || 0;
  const avgTotal = overview.currentAvg;
  const fillPct = avgTotal != null ? pctOf(avgTotal) : 0;
  const markerPct = pctOf(targetValue);
  const diff = avgTotal != null ? Math.round(avgTotal - targetValue) : null;
  const markerLabel = (target?.label || 'Target').split(' — ')[0] || 'Target';

  const selectValue = target?.custom
    ? 'custom'
    : TARGET_OPTIONS.find((o) => o.label === target?.label)?.label || TARGET_OPTIONS[4].label;
  const selectedTrack = TARGET_OPTIONS.find((o) => o.label === selectValue);
  const selectDisplay = selectValue === 'custom'
    ? 'Custom target…'
    : selectedTrack
      ? `${selectedTrack.label} (${selectedTrack.value}+ / Band ${selectedTrack.band})`
      : selectValue;

  const onTargetChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      const next = { value: Number(target.value) || 2850, band: target.band || 2, label: 'Custom', custom: true };
      setTarget(next);
      return;
    }
    const opt = TARGET_OPTIONS.find((o) => o.label === val);
    if (!opt) return;
    const next = { value: opt.value, band: opt.band, label: opt.label, custom: false };
    setTarget(next);
  };

  const onCustomInput = (e) => {
    const raw = Math.round((Number(e.target.value) || 0) / 10) * 10;
    const value = Math.max(1200, Math.min(3600, raw));
    const next = { value, band: target.band || 2, label: 'Custom', custom: true };
    setTarget(next);
  };

  const renderTable = (rows, kind) => (
    <table className="ucat-ws-table">
      <thead>
        <tr>
          <th>{kind === 'past' ? 'Paper' : 'Test'}</th>
          <th>VR</th>
          <th>DM</th>
          <th>QR</th>
          <th>SJT</th>
          <th>Total</th>
          <th>Mode</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const paper = row.papers?.verbal_reasoning;
          const unlocked = isPartUnlocked ? isPartUnlocked(paper || row) : true;
          const { scored, status } = row;
          return (
            <tr key={row.startId || row.title}>
              <td data-label={kind === 'past' ? 'Paper' : 'Test'} className="ucat-ws-cell-strong">
                {sittingLabel(row, row.index, kind)}
                {!unlocked && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ucat-ink-faint)' }}>Locked</span>
                )}
              </td>
              <td data-label="VR" className="ucat-ws-cell-mono">
                {scored.subtests.verbal_reasoning?.scaled ?? '—'}
                <div style={{ fontFamily: 'var(--ucat-mono)', fontWeight: 400, fontSize: 11, color: 'var(--ucat-ink-faint)' }}>
                  {formatPaperRaw(scored.subtests.verbal_reasoning?.correct, scored.subtests.verbal_reasoning?.total)}
                </div>
              </td>
              <td data-label="DM" className="ucat-ws-cell-mono">
                {scored.subtests.decision_making?.scaled ?? '—'}
                <div style={{ fontFamily: 'var(--ucat-mono)', fontWeight: 400, fontSize: 11, color: 'var(--ucat-ink-faint)' }}>
                  {formatPaperRaw(scored.subtests.decision_making?.correct, scored.subtests.decision_making?.total)}
                </div>
              </td>
              <td data-label="QR" className="ucat-ws-cell-mono">
                {scored.subtests.quantitative_reasoning?.scaled ?? '—'}
                <div style={{ fontFamily: 'var(--ucat-mono)', fontWeight: 400, fontSize: 11, color: 'var(--ucat-ink-faint)' }}>
                  {formatPaperRaw(scored.subtests.quantitative_reasoning?.correct, scored.subtests.quantitative_reasoning?.total)}
                </div>
              </td>
              <td data-label="SJT" className="ucat-ws-cell-mono">
                {formatUcatBand(scored.band)}
                <div style={{ fontFamily: 'var(--ucat-mono)', fontWeight: 400, fontSize: 11, color: 'var(--ucat-ink-faint)' }}>
                  {formatPaperRaw(scored.subtests.situational_judgement?.correct, scored.subtests.situational_judgement?.total)}
                </div>
              </td>
              <td data-label="Total" className="ucat-ws-cell-mono">
                {formatUcatTotal(scored.total)}
              </td>
              <td data-label="Mode">
                <ModeCell mode={scored.mode} status={status} />
              </td>
              <td data-label="Status">
                <StatusPill status={status} />
              </td>
              <td data-label="">
                {status === 'done' ? (
                  <div className="ucat-ws-actions">
                    <button type="button" className="ucat-ws-start-btn ghost" onClick={() => handleReview(row)}>
                      Review
                    </button>
                    <button
                      type="button"
                      className="ucat-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : status === 'progress' ? (
                  <div className="ucat-ws-actions">
                    <button type="button" className="ucat-ws-start-btn mid" onClick={() => handleContinue(row)}>
                      Continue
                    </button>
                    <button
                      type="button"
                      className="ucat-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="ucat-ws-start-btn"
                    onClick={() => {
                      if (!unlocked) {
                        onRequestUpgrade?.(paper);
                        return;
                      }
                      startSitting(row);
                    }}
                  >
                    Start
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderTopicsTable = (params) => {
    if (!params?.length) return null;
    return (
      <table className="ucat-ws-table">
        <thead>
          <tr>
            <th>Topic</th>
            <th>Questions</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {params.map((param) => {
            const done = completedList.includes(param.paper_id);
            const action = topicRowAction(topicAttemptRowFromMap(dbPapers, param.paper_id), done);
            const status = action.status;
            const title = param.title || param.paper_id;
            return (
              <tr key={param.paper_id}>
                <td data-label="Topic" className="ucat-ws-cell-strong">
                  {title}
                </td>
                <td data-label="Questions" className="ucat-ws-cell-mono">
                  {param.question_count != null ? param.question_count : '—'}
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  <button
                    type="button"
                    className={action.resume ? 'ucat-ws-start-btn mid' : 'ucat-ws-start-btn'}
                    onClick={() => onStartTopicPaper?.('UCAT', param, {
                      forceNew: action.forceNew,
                      resume: action.resume,
                    })}
                  >
                    {action.label}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const BreakdownRow = ({ name, meta, score, pct, goto, staticRow }) => {
    const inner = (
      <>
        <div className="ucat-ws-breakdown-name">{name}</div>
        <div className="ucat-ws-breakdown-bar-wrap">
          <div className="ucat-ws-breakdown-bar">
            <div className="ucat-ws-breakdown-bar-fill" style={{ width: `${pct || 0}%` }} />
          </div>
          <div className="ucat-ws-breakdown-meta">{meta}</div>
        </div>
        <div className="ucat-ws-breakdown-score">{score}</div>
        {!staticRow && <div className="ucat-ws-breakdown-arrow">›</div>}
      </>
    );
    if (staticRow) {
      return <div className="ucat-ws-breakdown-row static">{inner}</div>;
    }
    return (
      <button type="button" className="ucat-ws-breakdown-row" onClick={() => goTo(goto)}>
        {inner}
      </button>
    );
  };

  return (
    <div className="ucat-ws">
      <aside className={`ucat-ws-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="ucat-ws-sidebar-head">
          <span className="ucat-ws-name">UCAT Workspace</span>
          <button
            type="button"
            className="ucat-ws-icon-btn"
            title="Collapse sidebar"
            onClick={() => (mobileOpen ? setMobileOpen(false) : setCollapsed((c) => !c))}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
              <line x1="9" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <nav className="ucat-ws-nav">
          <button
            type="button"
            className={`ucat-ws-nav-item ${page === 'overview' ? 'active' : ''}`}
            onClick={() => goTo('overview')}
          >
            <svg className="ucat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="ucat-ws-nav-label">Overview</span>
          </button>

          <PracticeTopicsNav prefix="ucat-ws" page={page} onGo={goTo} />
          <PracticeTopicsNav
            prefix="ucat-ws"
            page={page}
            onGo={goTo}
            label={SUBTOPIC_PAPERS_LABEL}
            pages={SUBTOPIC_TIER_PAGES}
          />

          <div className="ucat-ws-nav-group">
            <div className="ucat-ws-nav-item ucat-ws-nav-parent">
              <svg className="ucat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6L12 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              <span className="ucat-ws-nav-label">Mock Tests</span>
            </div>
            <div className="ucat-ws-nav-sub">
              {[
                ['mock-beginnings', 'Beginnings', 1],
                ['mock-foundations', 'Foundations', 2],
                ['mock-beyond', 'Beyond', 3],
              ].map(([id, label, tier]) => (
                <button
                  key={id}
                  type="button"
                  className={`ucat-ws-nav-subitem ${page === id ? 'active' : ''}`}
                  onClick={() => goTo(id)}
                >
                  <svg className="ucat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                    <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                    <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
                  </svg>
                  <span className="ucat-ws-nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {[
            ['mock-beginnings', 'Beginnings', 1],
            ['mock-foundations', 'Foundations', 2],
            ['mock-beyond', 'Beyond', 3],
          ].map(([id, label, tier]) => (
            <button
              key={`c-${id}`}
              type="button"
              className={`ucat-ws-nav-item ucat-ws-collapsed-tier ${page === id ? 'active' : ''}`}
              title={label}
              onClick={() => goTo(id)}
            >
              <svg className="ucat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
              </svg>
              <span className="ucat-ws-nav-label">{label}</span>
            </button>
          ))}

          <button
            type="button"
            className={`ucat-ws-nav-item ${page === 'past' ? 'active' : ''}`}
            onClick={() => goTo('past')}
          >
            <svg className="ucat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <path d="M4 19.5V6a2 2 0 012-2h10.5a1.5 1.5 0 011.5 1.5V19a1 1 0 01-1 1H6.5A2.5 2.5 0 014 19.5z" stroke="currentColor" strokeWidth="1.6" />
              <line x1="8" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="ucat-ws-nav-label">Past Papers</span>
          </button>
          <button
            type="button"
            className={`ucat-ws-nav-item ${page === 'score-calculator' ? 'active' : ''}`}
            onClick={() => goTo('score-calculator')}
          >
            <svg className="ucat-ws-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <rect x="4" y="2" width="16" height="20" rx="2" />
              <line x1="8" y1="6" x2="16" y2="6" />
              <line x1="8" y1="10" x2="8.01" y2="10" />
              <line x1="12" y1="10" x2="12.01" y2="10" />
              <line x1="16" y1="10" x2="16.01" y2="10" />
              <line x1="8" y1="14" x2="8.01" y2="14" />
              <line x1="12" y1="14" x2="12.01" y2="14" />
              <line x1="16" y1="14" x2="16.01" y2="14" />
              <line x1="8" y1="18" x2="8.01" y2="18" />
              <line x1="12" y1="18" x2="16" y2="18" />
            </svg>
            <span className="ucat-ws-nav-label">Score calculator</span>
          </button>
          <button
            type="button"
            className={`ucat-ws-nav-item ${page === 'med-schools' ? 'active' : ''}`}
            onClick={() => goTo('med-schools')}
          >
            <svg className="ucat-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <path d="M4 20V9.5L12 4l8 5.5V20" stroke="currentColor" strokeWidth="1.6" />
              <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.6" />
              <path d="M4 20h16" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="ucat-ws-nav-label">Med Schools</span>
          </button>
        </nav>
      </aside>

      <main className="ucat-ws-main">
        <div className="ucat-ws-topbar-mobile">
          <button type="button" className="ucat-ws-icon-btn" onClick={() => setMobileOpen((o) => !o)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <span>{PAGES[page]}</span>
        </div>

        <div className="ucat-ws-back-row">
          <button type="button" className="ucat-ws-icon-btn ucat-ws-back" title="Back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {page === 'overview' && (
          <section className="ucat-ws-page">
            <div className="ucat-ws-page-header">
              <h1 className="ucat-ws-page-title">Overview</h1>
            </div>

            <div className="ucat-ws-target-card">
              <div className="ucat-ws-target-field ucat-ws-target-field--university">
                <label htmlFor="ucat-track">Target university</label>
                <div className="ucat-ws-target-select-shell">
                  <span className="ucat-ws-target-select-text" aria-hidden="true">
                    {selectDisplay}
                  </span>
                  <select
                    id="ucat-track"
                    className="ucat-ws-target-select"
                    value={selectValue}
                    onChange={onTargetChange}
                  >
                    {TARGET_OPTIONS.map((o) => (
                      <option key={o.label} value={o.label}>
                        {o.label} ({o.value}+ / Band {o.band})
                      </option>
                    ))}
                    <option value="custom">Custom target…</option>
                  </select>
                </div>
              </div>
              <div className="ucat-ws-target-field">
                <label htmlFor="ucat-custom">Custom score</label>
                <input
                  id="ucat-custom"
                  type="number"
                  className="ucat-ws-target-input"
                  min={1200}
                  max={3600}
                  step={10}
                  placeholder="/ 3600"
                  hidden={!target.custom}
                  value={target.custom ? target.value : ''}
                  onChange={onCustomInput}
                />
              </div>
              <div className="ucat-ws-target-readout">
                <span className="num">
                  {targetValue.toLocaleString()}
                  <span style={{ color: 'var(--ucat-ink-faint)', fontWeight: 500 }}>/3600</span>
                </span>
                <div className="lbl">UCAT score target</div>
                <div style={{ fontSize: 11, color: 'var(--ucat-ink-faint)', marginTop: 2 }}>
                  SJT target: Band {target.band ?? 2}
                </div>
              </div>
            </div>

            <div className="ucat-ws-section">
              <div className="ucat-ws-progress-status" style={{ marginBottom: 8 }}>
                {avgTotal == null ? (
                  'No scored sittings yet — complete a mock or past paper to see progress.'
                ) : diff >= 0 ? (
                  <>
                    <b>{diff} points</b> above target — keep it up
                  </>
                ) : (
                  <>
                    <b>{Math.abs(diff)} points</b> below target
                  </>
                )}
              </div>
              <div className="ucat-ws-progress-track">
                <div className="ucat-ws-progress-fill" style={{ width: `${fillPct}%` }} />
                <div
                  className="ucat-ws-progress-marker"
                  data-label={markerLabel}
                  style={{ left: `${markerPct}%` }}
                />
              </div>
              <div className="ucat-ws-breakdown-meta">
                Current average:{' '}
                <b style={{ color: 'var(--ucat-ink)', fontFamily: 'var(--ucat-mono)' }}>
                  {formatUcatTotal(avgTotal)}
                </b>
                {overview.scoredAttempts > 0
                  ? ` · based on ${overview.scoredAttempts} scored sitting${overview.scoredAttempts === 1 ? '' : 's'}`
                  : ''}
                {overview.modalBand != null ? ` · SJT ${formatUcatBand(overview.modalBand)}` : ''}
              </div>
            </div>

            <div className="ucat-ws-stat-strip">
              <div className="ucat-ws-stat-cell">
                <span className="ucat-ws-stat-value">{overview.totalQuestions.toLocaleString()}</span>
                <span className="ucat-ws-stat-label">Total questions</span>
              </div>
              <div className="ucat-ws-stat-cell">
                <span className="ucat-ws-stat-value">{overview.attempted.toLocaleString()}</span>
                <span className="ucat-ws-stat-label">Questions attempted</span>
              </div>
              <div className="ucat-ws-stat-cell">
                <span className="ucat-ws-stat-value">
                  {overview.successRate != null ? `${overview.successRate}%` : '—'}
                </span>
                <span className="ucat-ws-stat-label">Success rate</span>
              </div>
              <div className="ucat-ws-stat-cell">
                <span className="ucat-ws-stat-value">{formatUcatTotal(avgTotal)}</span>
                <span className="ucat-ws-stat-label">Average score</span>
              </div>
            </div>

            <div className="ucat-ws-section">
              <h2 className="ucat-ws-section-title">By tier</h2>
              <BreakdownRow
                name="Beginnings"
                meta={`${overview.beginnings.done} of ${overview.beginnings.total} mocks completed`}
                score={formatUcatTotal(overview.beginnings.avg)}
                pct={overview.beginnings.pct}
                goto="mock-beginnings"
              />
              <BreakdownRow
                name="Foundations"
                meta={`${overview.foundations.done} of ${overview.foundations.total} mocks completed`}
                score={formatUcatTotal(overview.foundations.avg)}
                pct={overview.foundations.pct}
                goto="mock-foundations"
              />
              <BreakdownRow
                name="Beyond"
                meta={`${overview.beyond.done} of ${overview.beyond.total} mocks completed`}
                score={formatUcatTotal(overview.beyond.avg)}
                pct={overview.beyond.pct}
                goto="mock-beyond"
              />
            </div>

            <div className="ucat-ws-section">
              <h2 className="ucat-ws-section-title">By type</h2>
              <BreakdownRow
                name="Practice Topics"
                meta={practiceTopicsRow.meta}
                score={practiceTopicsRow.score}
                pct={practiceTopicsRow.pct}
                goto="practice"
              />
              <BreakdownRow
                name="Mock Tests"
                meta={`${overview.mocks.done} of ${overview.mocks.total} mocks completed`}
                score={formatUcatTotal(overview.mocks.avg)}
                pct={overview.mocks.pct}
                goto="mock-all"
              />
              <BreakdownRow
                name="Past Papers"
                meta={`${overview.past.done} of ${overview.past.total} papers completed`}
                score={formatUcatTotal(overview.past.avg)}
                pct={overview.past.pct}
                goto="past"
              />
            </div>

            <PracticeTopicsOverview
              prefix="ucat-ws"
              topicsDone={topicsDone}
              topicCount={topicCount}
              papers={sectionProgress}
              onGo={goTo}
            />

            <div className="ucat-ws-section">
              <h2 className="ucat-ws-section-title">By mode</h2>
              <BreakdownRow
                name="Timed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.timed.count} of ${overview.timed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatUcatTotal(overview.timed.avg)}
                pct={overview.timed.pct}
                staticRow
              />
              <BreakdownRow
                name="Untimed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.untimed.count} of ${overview.untimed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatUcatTotal(overview.untimed.avg)}
                pct={overview.untimed.pct}
                staticRow
              />
            </div>
          </section>
        )}

        <PracticeTopicsPages
          page={page}
          prefix="ucat-ws"
          exam="UCAT"
          topics={(tier) => (
            topicPapersContent({
              prefix: "ucat-ws",
              tier,
              topicSections,
              topicStatus,
              renderTable: renderTopicsTable,
            })
          )}
          sectionPapers={sectionPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />
        <PracticeTopicsPages
          page={page}
          prefix="ucat-ws"
          exam="UCAT"
          label={SUBTOPIC_PAPERS_LABEL}
          allPage={SUBTOPIC_PAPERS_ALL_PAGE}
          tierPages={SUBTOPIC_TIER_PAGES}
          titles={SUBTOPIC_PAGE_TITLES}
          ofTier={subtopicPapersOfTier}
          papers={subtopicPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />

        {page === 'mock-all' && (
          <section className="ucat-ws-page">
            <div className="ucat-ws-page-header">
              <h1 className="ucat-ws-page-title">Mock Tests</h1>
            </div>
            {[
              ['Beginnings', beginnings],
              ['Foundations', foundations],
              ['Beyond', beyond],
            ].filter(([, rows]) => rows.length).map(([label, rows]) => (
              <div key={label} className="ucat-ws-section">
                <h2 className="ucat-ws-section-title">{label}</h2>
                {renderTable(rows, 'mock')}
              </div>
            ))}
          </section>
        )}

        {page === 'mock-beginnings' && (
          <section className="ucat-ws-page">
            <div className="ucat-ws-page-header">
              <h1 className="ucat-ws-page-title">Mock Tests: Beginnings</h1>
            </div>
            {renderTable(beginnings, 'mock')}
          </section>
        )}

        {page === 'mock-foundations' && (
          <section className="ucat-ws-page">
            <div className="ucat-ws-page-header">
              <h1 className="ucat-ws-page-title">Mock Tests: Foundations</h1>
            </div>
            {renderTable(foundations, 'mock')}
          </section>
        )}

        {page === 'mock-beyond' && (
          <section className="ucat-ws-page">
            <div className="ucat-ws-page-header">
              <h1 className="ucat-ws-page-title">Mock Tests: Beyond</h1>
            </div>
            {renderTable(beyond, 'mock')}
          </section>
        )}

        {page === 'past' && (
          <section className="ucat-ws-page">
            <div className="ucat-ws-page-header">
              <h1 className="ucat-ws-page-title">Past Papers</h1>
            </div>
            {renderTable(past, 'past')}
          </section>
        )}
        {page === 'score-calculator' && <UcatScoreCalculator />}
        {page === 'med-schools' && <MedSchoolsPage />}
      </main>

      <div className={`ucat-ws-toast ${toast ? 'show' : ''}`}>{toast}</div>
    </div>
  );
};

export default UcatWorkspace;
